import { useState, useEffect, useRef, useMemo } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import {
  ChevronRight,
  Key,
  Volume2,
  Settings,
  Play,
  Pause,
  Upload,
  Trash2,
  Plus,
  ArrowLeft,
  Edit,
  X,
  Check,
  ChevronUp,
  ChevronDown,
  Loader2,
  Copy,
  Shuffle,
  Zap,
  Clock,
  Target,
  Layers,
  Hash,
  BarChart3,
  MessageSquare,
  AlertCircle,
  Lock,
  Unlock,
  GripVertical
} from 'lucide-react'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { avatarApi, monitoringApi, API_BASE_URL } from '../services/api'
import { useAuth } from '../context/AuthContext'
import './AvatarDetails.css'

// Azure blob URL for audio files
const BLOB_URL = 'https://callbot.blob.core.windows.net/sounds/'

// Sortable Action Item Component for drag and drop
function SortableActionItem({ action, idx, step, voiceMode, openActionModal, getLabelDisplay, isDraggable, voiceSetFilesByCategory }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({
    id: action.id,
    disabled: !isDraggable
  })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 'auto'
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`action-row ${action.action_type} ${isDragging ? 'dragging' : ''}`}
      onClick={() => openActionModal(action, step.value)}
    >
      {/* Drag Handle */}
      <div className="action-order">
        {isDraggable ? (
          <div className="drag-handle" {...attributes} {...listeners} onClick={(e) => e.stopPropagation()}>
            <GripVertical size={14} />
          </div>
        ) : (
          <div className="drag-handle disabled">
            <GripVertical size={14} />
          </div>
        )}
      </div>

      {/* Action Content */}
      <div className="action-content">
        {action.action_type === 'regular' && (
          <>
            <span className="action-index">{action.order_idx}</span>
            <span className={`condition-badge ${idx === 0 ? 'if' : action.array === '- no condition -' ? 'else' : 'else-if'}`}>
              {idx === 0 ? 'IF' : action.array === '- no condition -' ? 'ELSE' : 'ELIF'}
            </span>
            {action.array !== '- no condition -' && (
              <>
                <span className="condition-name">{getLabelDisplay(action.array)}</span>
                <span className="action-separator">→</span>
              </>
            )}
          </>
        )}

        {action.action_type === 'on_silence' && (
          <span className="action-type-label silence">⏱ Silence #{action.silence_counter}</span>
        )}

        {action.action_type === 'on_not_interested' && (
          <span className="action-type-label not-interested">✕ Not Interested</span>
        )}

        {/* Audio display */}
        {voiceMode === 'classic' ? (
          (action.audio_1 !== '- none -' || action.audio_2 !== '- none -' || action.audio_3 !== '- none -') && (
            <span className="audio-info">
              ♪ {[action.audio_1, action.audio_2, action.audio_3].filter(a => a && a !== '- none -').join(', ')}
            </span>
          )
        ) : (
          (action.audio_category_1 !== '- none -' || action.audio_category_2 !== '- none -' || action.audio_category_3 !== '- none -') && (
            <span className="audio-info">
              <Shuffle size={10} />
              {[action.audio_category_1, action.audio_category_2, action.audio_category_3].filter(a => a && a !== '- none -').join(', ')}
            </span>
          )
        )}

        {action.emit && (
          <span className="emit-info">
            → <strong>{action.emit}</strong>
          </span>
        )}
      </div>

      {/* Action Meta */}
      <div className="action-meta">
        {/* Modification Info */}
        {action.modified_by && (
          <span
            className="action-modified-info"
            title={`${action.last_action === 'created' ? 'Created' : 'Modified'} by ${action.modified_by} on ${new Date(action.modified_at).toLocaleString()}`}
          >
            <Clock size={10} />
            <span className="modified-user">{action.modified_by}</span>
            <span className="modified-date">{new Date(action.modified_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
          </span>
        )}
        <span
          title={action.can_interrupt === 0 || action.can_interrupt === false ? 'Non-interruptible' : 'Interruptible'}
          style={{
            opacity: action.can_interrupt === 0 || action.can_interrupt === false ? 1 : 0.3,
            marginRight: '4px'
          }}
        >
          {action.can_interrupt === 0 || action.can_interrupt === false ? <Lock size={12} /> : <Unlock size={12} />}
        </span>
        <span className={`action-type-tag ${action.action_type === 'regular' ? 'regular' : action.action_type === 'on_silence' ? 'silence' : 'not-interested'}`}>
          {action.action_type === 'regular' ? 'REG' : action.action_type === 'on_silence' ? 'SIL' : 'N/I'}
        </span>
        <button className="action-edit-btn" onClick={(e) => { e.stopPropagation(); openActionModal(action, step.value) }}>
          <Edit size={12} />
        </button>
      </div>
    </div>
  )
}

function AvatarDetails() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { user } = useAuth()
  const [avatar, setAvatar] = useState(null)
  const [activeTab, setActiveTab] = useState('licenses')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [dataReady, setDataReady] = useState(false)

  // ========== LICENSES TAB ==========
  const [licenses, setLicenses] = useState([])
  const [dialers, setDialers] = useState([])
  const [accessLinks, setAccessLinks] = useState([])
  const [selectedLicenses, setSelectedLicenses] = useState([])
  const [botStatus, setBotStatus] = useState([])

  // ========== ENGINE TAB ==========
  const [engineActions, setEngineActions] = useState([])
  const [sounds, setSounds] = useState([])
  const [soundsRecords, setSoundsRecords] = useState([])
  const [voiceSets, setVoiceSets] = useState([])
  const [voiceSetCategories, setVoiceSetCategories] = useState([])
  const [voiceSetFilesByCategory, setVoiceSetFilesByCategory] = useState({})
  const [voiceMode, setVoiceMode] = useState('modern')
  const [selectedVoiceSetId, setSelectedVoiceSetId] = useState(null)
  const [keywordMode, setKeywordMode] = useState('modern')
  const [scriptSteps, setScriptSteps] = useState([])
  const [events, setEvents] = useState([])
  const [tables, setTables] = useState([])
  const [stepIterationLimits, setStepIterationLimits] = useState([1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1])

  // Hello audio settings
  const [sayHelloOnPickup, setSayHelloOnPickup] = useState(true)
  const [helloAudioOne, setHelloAudioOne] = useState('- none -')
  const [helloAudioTwo, setHelloAudioTwo] = useState('- none -')
  const [helloTimingOne, setHelloTimingOne] = useState(0)
  const [helloTimingTwo, setHelloTimingTwo] = useState(0)
  const [helloCategoryOne, setHelloCategoryOne] = useState('- none -')
  const [helloCategoryTwo, setHelloCategoryTwo] = useState('- none -')

  // ========== SOUNDS TAB ==========
  const [soundFiles, setSoundFiles] = useState([])
  const [playingSound, setPlayingSound] = useState(null)
  const audioRef = useRef(null)
  const fileInputRef = useRef(null)

  // ========== MODAL STATE ==========
  const [showModal, setShowModal] = useState(false)
  const [modalType, setModalType] = useState('')
  const [modalMode, setModalMode] = useState('add')
  const [currentItem, setCurrentItem] = useState(null)
  const [formData, setFormData] = useState({})
  const [hasChanged, setHasChanged] = useState(false)

  // Searchable dropdown states
  const [audioSearch, setAudioSearch] = useState('')
  const [openDropdown, setOpenDropdown] = useState(null) // 'audio_1', 'audio_2', 'audio_3', 'emit', etc.

  // Arrays for engine conditions - now loaded dynamically from keyword_categories_v2 table
  const [conditionArrays, setConditionArrays] = useState(['- no condition -'])

  // Action types
  const actionTypes = [
    { value: 'regular', label: 'Regular' },
    { value: 'on_silence', label: 'On Silence' },
    { value: 'on_not_interested', label: 'On Not Interested' }
  ]

  // ========== COMPUTED STATS ==========
  const engineStats = useMemo(() => {
    const totalActions = engineActions.length
    const regularCount = engineActions.filter(a => a.action_type === 'regular').length
    const silenceCount = engineActions.filter(a => a.action_type === 'on_silence').length
    const notInterestedCount = engineActions.filter(a => a.action_type === 'on_not_interested').length
    const uniqueConditions = new Set(engineActions.filter(a => a.array && a.array !== '- no condition -').map(a => a.array))
    const stepsWithActions = scriptSteps.filter(s => s.actions?.length > 0).length

    return { totalActions, regularCount, silenceCount, notInterestedCount, uniqueConditions: uniqueConditions.size, stepsWithActions }
  }, [engineActions, scriptSteps])

  // License last used mapping from bot status
  const licenseLastUsedMap = useMemo(() => {
    const map = {}
    botStatus.forEach(bot => {
      const timestamp = bot.last_heartbeat || bot.updated_at || bot.last_seen
      if (timestamp && bot.license_id) {
        const time = new Date(timestamp).getTime()
        if (!map[bot.license_id] || time > map[bot.license_id]) {
          map[bot.license_id] = time
        }
      }
    })
    return map
  }, [botStatus])

  // Get formatted last used time for a license
  const getLicenseLastUsed = (licenseId) => {
    const timestamp = licenseLastUsedMap[licenseId]
    if (!timestamp) return null

    const now = Date.now()
    const diff = now - timestamp

    if (diff < 2 * 60 * 1000) return { text: 'Active', isActive: true }
    if (diff < 60 * 60 * 1000) return { text: `${Math.floor(diff / 60000)}m ago`, isActive: false }
    if (diff < 24 * 60 * 60 * 1000) return { text: `${Math.floor(diff / 3600000)}h ago`, isActive: false }
    if (diff < 7 * 24 * 60 * 60 * 1000) return { text: `${Math.floor(diff / 86400000)}d ago`, isActive: false }
    return { text: new Date(timestamp).toLocaleDateString(), isActive: false }
  }

  useEffect(() => {
    loadAvatar()
    loadDialers()
    loadAccessLinks()
    loadVoiceSets()
    loadKeywordCategories()
  }, [id])

  // Update tables when keyword categories are loaded
  useEffect(() => {
    setTables(conditionArrays.map(arr => ({ table: arr, label: arr })))
  }, [conditionArrays])

  useEffect(() => {
    if (avatar) {
      setVoiceMode(avatar.voice_mode || 'modern')
      setSelectedVoiceSetId(avatar.voice_set_id || null)
      setKeywordMode(avatar.keyword_mode || 'modern')
      setSayHelloOnPickup(avatar.say_hello_on_pickup !== false)
      setHelloAudioOne(avatar.hello_audio_one || '- none -')
      setHelloAudioTwo(avatar.hello_audio_two || '- none -')
      setHelloTimingOne(avatar.say_hello_timing_one || 0)
      setHelloTimingTwo(avatar.say_hello_timing_two || 0)
      setHelloCategoryOne(avatar.hello_audio_category_one || '- none -')
      setHelloCategoryTwo(avatar.hello_audio_category_two || '- none -')

      if (avatar.script_step_iterations_limit) {
        try {
          const limits = JSON.parse(avatar.script_step_iterations_limit)
          setStepIterationLimits(limits)
        } catch (e) {
          console.error('Failed to parse step limits:', e)
        }
      }

      loadTabData()
    }
  }, [avatar, activeTab])

  useEffect(() => {
    if (selectedVoiceSetId && voiceMode === 'modern') {
      loadVoiceSetCategories()
    }
  }, [selectedVoiceSetId, voiceMode])

  // ========== LOAD FUNCTIONS ==========
  const loadAvatar = async () => {
    setLoading(true)
    try {
      const res = await avatarApi.getById(id)
      const data = Array.isArray(res.data) ? res.data[0] : res.data
      setAvatar(data || null)
    } catch (error) {
      console.error('Failed to load avatar:', error)
      setAvatar(null)
    } finally {
      setLoading(false)
    }
  }

  const loadDialers = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/tables/dialers`)
      const data = await res.json()
      const allDialers = Array.isArray(data) ? data.filter(d => !d.archived) : []
      setDialers(allDialers)
    } catch (error) {
      console.error('Failed to load dialers:', error)
    }
  }

  // Get dialers linked to this avatar
  const linkedDialers = useMemo(() => {
    return dialers.filter(d => String(d.avatar_id) === String(id))
  }, [dialers, id])

  const loadAccessLinks = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/tables/links`)
      const data = await res.json()
      if (data[0]?.links) {
        const links = JSON.parse(data[0].links)
        setAccessLinks(Array.isArray(links) ? links : [])
      }
    } catch (error) {
      console.error('Failed to load access links:', error)
    }
  }

  const loadVoiceSets = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/voice-sets`)
      const data = await res.json()
      setVoiceSets(Array.isArray(data) ? data : [])
    } catch (error) {
      console.error('Failed to load voice sets:', error)
    }
  }

  const loadKeywordCategories = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/keyword-categories?include_keywords=false`)
      const data = await res.json()
      if (Array.isArray(data)) {
        // Extract category names and add '- no condition -' at the start
        const categoryNames = data.map(cat => cat.name)
        setConditionArrays(['- no condition -', ...categoryNames])
      }
    } catch (error) {
      console.error('Failed to load keyword categories:', error)
    }
  }

  const loadVoiceSetCategories = async () => {
    if (!selectedVoiceSetId) {
      setVoiceSetCategories(['- none -'])
      return
    }
    try {
      const res = await fetch(`${API_BASE_URL}/voice-sets/${selectedVoiceSetId}/categories`)
      const data = await res.json()
      const cats = ['- none -'].concat(data.map(c => c.name))
      setVoiceSetCategories(cats)
    } catch (error) {
      console.error('Failed to load categories:', error)
      setVoiceSetCategories(['- none -'])
    }
  }

  const loadTabData = async () => {
    if (activeTab === 'licenses') {
      loadLicenses()
      loadBotStatus()
    } else if (activeTab === 'engine') {
      await loadSoundsRecords()
      loadEngineActions()
      loadEvents()
    } else if (activeTab === 'sounds') {
      loadSoundFiles()
      loadSoundsRecords()
    }
  }

  const loadBotStatus = async () => {
    try {
      const res = await monitoringApi.getAllStatus()
      setBotStatus(Array.isArray(res.data) ? res.data : [])
    } catch (error) {
      console.error('Failed to load bot status:', error)
      setBotStatus([])
    }
  }

  const loadLicenses = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/tables/licenses?field=avatar_id&val=${id}`)
      const data = await res.json()
      setLicenses(Array.isArray(data) ? data : [])
    } catch (error) {
      console.error('Failed to load licenses:', error)
    }
  }

  const loadEngineActions = async () => {
    setDataReady(false)
    try {
      const res = await fetch(`${API_BASE_URL}/tables/engine_actions?field=avatar_id&val=${id}`)
      const data = await res.json()
      const actions = Array.isArray(data) ? data : []
      setEngineActions(actions)

      // Load voice set metadata if in modern mode
      let voiceSetMetadata = {}
      const filesByCategory = {}
      if (voiceMode === 'modern' && selectedVoiceSetId) {
        try {
          const metaRes = await fetch(`${API_BASE_URL}/voice-sets/${selectedVoiceSetId}/files-with-metadata`)
          const metaData = await metaRes.json()
          if (metaData.files) {
            // Build category -> transcription map (use first file's transcription per category)
            // Also build category -> all files map
            metaData.files.forEach(file => {
              if (file.category_name && file.transcription && !voiceSetMetadata[file.category_name]) {
                voiceSetMetadata[file.category_name] = file.transcription
              }
              if (file.category_name) {
                if (!filesByCategory[file.category_name]) filesByCategory[file.category_name] = []
                filesByCategory[file.category_name].push({
                  display_name: file.display_name || file.filename,
                  transcription: file.transcription || ''
                })
              }
            })
          }
        } catch (e) {
          console.log('[AvatarDetails] Could not load voice set metadata:', e.message)
        }
      }
      setVoiceSetFilesByCategory(filesByCategory)

      // Organize into script steps (1-8)
      const steps = []
      for (let i = 1; i <= 8; i++) {
        const stepActions = actions
          .filter(a => a.step_number === i)
          .sort((a, b) => {
            if (a.array === '- no condition -') return 1
            if (b.array === '- no condition -') return -1
            return (a.order_idx || 0) - (b.order_idx || 0)
          })

        let previousSound = ''
        if (i === 1) {
          previousSound = 'Hello?'
        } else {
          const prevAction = actions.find(a =>
            a.step_number === (i - 1) &&
            a.action_type === 'regular' &&
            a.array === '- no condition -'
          )
          if (prevAction) {
            if (voiceMode === 'modern' && selectedVoiceSetId) {
              // Modern mode: Collect all transcriptions from all categories
              const cats = [prevAction.audio_category_1, prevAction.audio_category_2, prevAction.audio_category_3]
                .filter(c => c && c !== '- none -')
              const allLines = []
              cats.forEach(cat => {
                const files = filesByCategory[cat]
                if (files && files.length > 0) {
                  files.forEach(f => {
                    if (f.transcription) allLines.push(f.transcription)
                  })
                } else if (voiceSetMetadata[cat]) {
                  allLines.push(voiceSetMetadata[cat])
                }
              })
              const uniqueLines = [...new Set(allLines)]
              previousSound = uniqueLines.length > 0 ? uniqueLines.join(' | ') : (cats.join(', ') || '????')
            } else {
              // Classic mode: Get transcription from sounds table
              const audio = soundsRecords.find(s => s.name === prevAction.audio_1)
              previousSound = audio?.transcription || prevAction.audio_1 || '????'
            }
          }
        }

        // Check if any action in this step has use_whisper enabled
        const useWhisper = stepActions.some(a => a.use_whisper === 1 || a.use_whisper === true)

        steps.push({
          value: i,
          text: `Script Step ${i}`,
          actions: stepActions,
          previousSound,
          useWhisper
        })
      }
      setScriptSteps(steps)
      setDataReady(true)
    } catch (error) {
      console.error('Failed to load engine actions:', error)
      setDataReady(true)
    }
  }

  const loadSoundsRecords = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/tables/sounds?field=avatar_id&val=${id}`)
      const data = await res.json()
      const records = Array.isArray(data) ? data : []
      setSoundsRecords(records)
      setSounds(['- none -'].concat(records.map(s => s.name)))
      return records
    } catch (error) {
      console.error('Failed to load sounds:', error)
      return []
    }
  }

  const loadSoundFiles = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/blob/getFiles/${id}`)
      const data = await res.json()
      setSoundFiles(Array.isArray(data) ? data : [])
    } catch (error) {
      console.error('Failed to load sound files:', error)
    }
  }

  const loadEvents = async () => {
    try {
      const licRes = await fetch(`${API_BASE_URL}/tables/licenses?field=avatar_id&val=${id}`)
      const licData = await licRes.json()
      if (Array.isArray(licData) && licData.length > 0 && licData[0]?.dialer_id) {
        const dispRes = await fetch(`${API_BASE_URL}/tables/dispositions?field=dialer_id&val=${licData[0].dialer_id}`)
        const dispData = await dispRes.json()
        const dispositionEvents = Array.isArray(dispData) ? dispData.map(d => d.event_tag).filter(Boolean) : []
        // Add built-in events (matching Angular implementation)
        const eventList = ['back-one-step', 'transfer-call'].concat(dispositionEvents).concat(['engine-do-not-call-again', 'engine-not-interested'])
        setEvents(eventList)
        return eventList
      } else {
        // Even without dispositions, include built-in events
        const builtInEvents = ['back-one-step', 'transfer-call', 'engine-do-not-call-again', 'engine-not-interested']
        setEvents(builtInEvents)
        return builtInEvents
      }
    } catch (error) {
      console.error('Failed to load events:', error)
      setEvents([])
      return []
    }
  }

  // ========== SEARCHABLE SELECT COMPONENT ==========
  const SearchableSelect = ({ options, value, onChange, placeholder, field, disabled }) => {
    const isOpen = openDropdown === field
    const [localSearch, setLocalSearch] = useState('')

    const filteredOptions = options.filter(opt =>
      opt.toLowerCase().includes(localSearch.toLowerCase())
    )

    const handleSelect = (opt) => {
      onChange(opt)
      setOpenDropdown(null)
      setLocalSearch('')
    }

    const handleToggle = (e) => {
      e.stopPropagation() // Prevent modal body click from closing dropdown
      if (disabled) return
      if (isOpen) {
        setOpenDropdown(null)
        setLocalSearch('')
      } else {
        setOpenDropdown(field)
      }
    }

    return (
      <div className="searchable-select" onClick={(e) => e.stopPropagation()}>
        <div
          className={`searchable-select-trigger ${isOpen ? 'open' : ''}`}
          onClick={handleToggle}
          style={disabled ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
        >
          <span className={`searchable-select-value ${!value || value === '- none -' ? 'placeholder' : ''}`}>
            {value && value !== '- none -' ? value : placeholder}
          </span>
          <ChevronDown size={14} style={{ transform: isOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
        </div>
        {isOpen && (
          <div className="searchable-select-dropdown" onClick={(e) => e.stopPropagation()}>
            <div className="searchable-select-search">
              <input
                type="text"
                placeholder="Search..."
                value={localSearch}
                onChange={(e) => setLocalSearch(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                autoFocus
              />
            </div>
            <div className="searchable-select-options">
              {filteredOptions.length === 0 ? (
                <div className="searchable-select-option no-results">No matches found</div>
              ) : (
                filteredOptions.map((opt, idx) => (
                  <div
                    key={`${opt}-${idx}`}
                    className={`searchable-select-option ${opt === value ? 'selected' : ''}`}
                    onClick={(e) => { e.stopPropagation(); handleSelect(opt) }}
                  >
                    {opt}
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </div>
    )
  }

  // ========== AVATAR UPDATE ==========
  const updateAvatar = async (updates = {}) => {
    const avatarUpdate = {
      id: parseInt(id),
      say_hello_on_pickup: sayHelloOnPickup,
      hello_audio_one: helloAudioOne,
      hello_audio_two: helloAudioTwo,
      hello_audio_category_one: helloCategoryOne,
      hello_audio_category_two: helloCategoryTwo,
      say_hello_timing_one: helloTimingOne,
      say_hello_timing_two: helloTimingTwo,
      voice_mode: voiceMode,
      voice_set_id: selectedVoiceSetId,
      keyword_mode: keywordMode,
      script_step_iterations_limit: JSON.stringify(stepIterationLimits),
      ...updates
    }

    try {
      await fetch(`${API_BASE_URL}/tables/avatars/update/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(avatarUpdate)
      })
    } catch (error) {
      console.error('Failed to update avatar:', error)
    }
  }

  // ========== ENCRYPTION UTILITIES - Match backend common.js EXACTLY ==========
  /**
   * Generate random token - matches common.js generateToken()
   * Uses lowercase letters + digits, 16 characters
   */
  const generateToken = () => {
    const chars = 'abcdefghijklmnopqrstuvwxyz0123456789'
    let token = ''
    for (let i = 0; i < 16; i++) {
      // Using Math.round like Angular/backend does
      token += chars.charAt(Math.round(Math.random() * (chars.length - 1)))
    }
    return token
  }

  /**
   * Encrypt text with salt - matches common.js crypt() EXACTLY
   * This is copied directly from the backend common.js
   */
  const crypt = (text, salt) => {
    const textToChars = (text) => text.split("").map((c) => c.charCodeAt(0))
    const byteHex = (n) => ("0" + Number(n).toString(16)).substr(-2)
    const applySaltToChar = (code) => textToChars(salt).reduce((a, b) => a ^ b, code)

    return text
      .split("")
      .map(textToChars)
      .map(applySaltToChar)
      .map(byteHex)
      .join("")
  }

  // The encryption key - MUST match backend .env ENCRYPTION_KEY
  const ENCRYPTION_KEY = 'theFutureIsBright'

  /**
   * Generate key_string from token
   * This is what the bot uses to authenticate
   */
  const generateKeyString = (token) => {
    return crypt(token, ENCRYPTION_KEY)
  }

  // ========== LICENSE FUNCTIONS ==========
  const openLicenseModal = (license = null) => {
    setModalType('license')
    setModalMode(license ? 'edit' : 'add')
    setCurrentItem(license)

    if (license) {
      // Edit mode - populate with existing data
      // Parse voice_set_pool if it's a JSON string
      let voiceSetPool = [];
      if (license.voice_set_pool) {
        try {
          voiceSetPool = typeof license.voice_set_pool === 'string'
            ? JSON.parse(license.voice_set_pool)
            : license.voice_set_pool;
        } catch (e) {
          console.error('Failed to parse voice_set_pool:', e);
          voiceSetPool = [];
        }
      }
      setFormData({ ...license, voice_set_pool: voiceSetPool, _showBotConfig: false })
    } else {
      // Add mode - pre-select linked dialer if available
      const linkedDialer = dialers.find(d => String(d.avatar_id) === String(id))
      setFormData({
        dialer_id: linkedDialer?.id || '',
        access_link: accessLinks[0] || '',
        user: '',
        password: '',
        campaign: '',
        // Bot configuration defaults
        login_method: 'double-merged',
        transfer_method: 'd1',
        xfer_disposition_selector: 'call-transfered',
        transcriber_type: 'VOSK',
        dont_wait: true,
        comment_login_submit_btn: true,
        comment_2nd_hello_logic: true,
        cmnt_40sec_call_limit_check: false,
        dispo_in_cb_after_40sec: false,
        debug_mode: false,
        number_of_audios: 1,
        voice_set_pool: [],
        _showBotConfig: false
      })
    }
    setHasChanged(false)
    setShowModal(true)
  }

  const saveLicense = async () => {
    setSaving(true)
    try {
      const dialer = dialers.find(d => d.id == formData.dialer_id)
      const licenseData = {
        user: formData.user,
        password: formData.password,
        access_link: formData.access_link,
        dialer: dialer?.name || '',
        dialer_id: parseInt(formData.dialer_id),
        campaign: formData.campaign,
        avatar_id: parseInt(id),
        // Bot configuration
        login_method: formData.login_method || 'double-merged',
        transfer_method: formData.transfer_method || 'd1',
        xfer_disposition_selector: formData.xfer_disposition_selector || 'call-transfered',
        transcriber_type: formData.transcriber_type || 'VOSK',
        dont_wait: formData.dont_wait !== false && formData.dont_wait !== 0 ? 1 : 0,
        comment_login_submit_btn: formData.comment_login_submit_btn !== false && formData.comment_login_submit_btn !== 0 ? 1 : 0,
        comment_2nd_hello_logic: formData.comment_2nd_hello_logic !== false && formData.comment_2nd_hello_logic !== 0 ? 1 : 0,
        cmnt_40sec_call_limit_check: formData.cmnt_40sec_call_limit_check === true || formData.cmnt_40sec_call_limit_check === 1 ? 1 : 0,
        dispo_in_cb_after_40sec: formData.dispo_in_cb_after_40sec === true || formData.dispo_in_cb_after_40sec === 1 ? 1 : 0,
        debug_mode: formData.debug_mode === true || formData.debug_mode === 1 ? 1 : 0,
        number_of_audios: formData.number_of_audios || 1,
        // Voice Set Pool (JSON array for per-call rotation)
        voice_set_pool: formData.voice_set_pool && formData.voice_set_pool.length > 0
          ? JSON.stringify(formData.voice_set_pool)
          : null
      }

      if (modalMode === 'add') {
        // Generate token and key_string EXACTLY like common.js
        const token = generateToken()
        const keyString = generateKeyString(token)

        licenseData.token = token
        licenseData.key_string = keyString

        console.log('[AvatarDetails] Creating license with:')
        console.log('  token:', token, '(length:', token.length, ')')
        console.log('  key_string:', keyString)

        await fetch(`${API_BASE_URL}/tables/licenses/add`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(licenseData)
        })
      } else {
        await fetch(`${API_BASE_URL}/tables/licenses/update/${currentItem.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...licenseData, id: currentItem.id })
        })
      }

      loadLicenses()
      setShowModal(false)
    } catch (error) {
      console.error('Failed to save license:', error)
    } finally {
      setSaving(false)
    }
  }

  const deleteLicense = async (license) => {
    if (!window.confirm('Delete this license?')) return
    try {
      await fetch(`${API_BASE_URL}/tables/licenses/delete?field=id&val=${license.id}`, { method: 'DELETE' })
      loadLicenses()
      setShowModal(false)
    } catch (error) {
      console.error('Failed to delete license:', error)
    }
  }

  const deleteSelectedLicenses = async () => {
    if (!window.confirm(`Delete ${selectedLicenses.length} licenses?`)) return
    setSaving(true)
    for (const lic of selectedLicenses) {
      try {
        await fetch(`${API_BASE_URL}/tables/licenses/delete?field=id&val=${lic.id}`, { method: 'DELETE' })
      } catch (error) {
        console.error('Failed to delete:', error)
      }
    }
    setSelectedLicenses([])
    setSaving(false)
    loadLicenses()
  }

  const toggleLicenseSelection = (lic) => {
    if (selectedLicenses.some(l => l.id === lic.id)) {
      setSelectedLicenses(selectedLicenses.filter(l => l.id !== lic.id))
    } else {
      setSelectedLicenses([...selectedLicenses, lic])
    }
  }

  // ========== ENGINE ACTION FUNCTIONS ==========
  const openActionModal = async (action = null, stepNumber = 1) => {
    // Ensure events are loaded
    if (events.length === 0) {
      await loadEvents()
    }

    setModalType('action')
    setModalMode(action ? 'edit' : 'add')
    setCurrentItem(action)
    setOpenDropdown(null) // Reset any open dropdowns
    setFormData(action ? {
      ...action,
      audio_category_1: action.audio_category_1 || '- none -',
      audio_category_2: action.audio_category_2 || '- none -',
      audio_category_3: action.audio_category_3 || '- none -',
      interrupt_audio_category: action.interrupt_audio_category || '- none -',
      continue_audio_category: action.continue_audio_category || '- none -',
      can_interrupt: action.can_interrupt !== undefined ? action.can_interrupt : 1
    } : {
      avatar_id: parseInt(id),
      step_number: stepNumber,
      action_type: 'regular',
      array: '- no condition -',
      audio_1: '- none -',
      audio_2: '- none -',
      audio_3: '- none -',
      audio_category_1: '- none -',
      audio_category_2: '- none -',
      audio_category_3: '- none -',
      interrupt_audio_category: '- none -',
      continue_audio_category: '- none -',
      emit: '',
      silence_counter: 0,
      increase_step: false,
      can_interrupt: 1
    })
    setHasChanged(false)
    setShowModal(true)
  }

  const saveAction = async () => {
    setSaving(true)
    try {
      const actionData = {
        avatar_id: parseInt(id),
        step_number: formData.step_number,
        action_type: formData.action_type,
        array: formData.array,
        audio_1: formData.audio_1,
        audio_2: formData.audio_2,
        audio_3: formData.audio_3,
        audio_category_1: formData.audio_category_1 || '- none -',
        audio_category_2: formData.audio_category_2 || '- none -',
        audio_category_3: formData.audio_category_3 || '- none -',
        interrupt_audio_category: formData.interrupt_audio_category || '- none -',
        continue_audio_category: formData.continue_audio_category || '- none -',
        emit: formData.emit || '',
        silence_counter: formData.silence_counter || 0,
        increase_step: formData.increase_step || false,
        can_interrupt: formData.can_interrupt !== undefined ? (formData.can_interrupt ? 1 : 0) : 1
      }

      // Add user info for change tracking
      const dataWithUser = {
        ...actionData,
        _user_id: user?.id || null,
        _user_name: user?.username || null
      }

      if (modalMode === 'add') {
        await fetch(`${API_BASE_URL}/tables/engine_actions/add`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(dataWithUser)
        })
      } else {
        await fetch(`${API_BASE_URL}/tables/engine_actions/update/${currentItem.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...dataWithUser, id: currentItem.id })
        })
      }
      loadEngineActions()
      setShowModal(false)
    } catch (error) {
      console.error('Failed to save action:', error)
    } finally {
      setSaving(false)
    }
  }

  const deleteAction = async (action) => {
    if (!window.confirm('Delete this action?')) return
    try {
      const userName = user?.username || ''
      await fetch(`${API_BASE_URL}/tables/engine_actions/delete?field=id&val=${action.id}&user_name=${encodeURIComponent(userName)}`, { method: 'DELETE' })
      loadEngineActions()
      setShowModal(false)
    } catch (error) {
      console.error('Failed to delete:', error)
    }
  }

  const moveAction = async (stepActions, index, direction) => {
    if (stepActions[index].array === '- no condition -') return
    if (direction === 'up' && index === 0) return
    if (direction === 'down') {
      const nextItem = stepActions[index + 1]
      if (!nextItem || nextItem.array === '- no condition -') return
    }

    const swapIndex = direction === 'up' ? index - 1 : index + 1
    const temp = stepActions[index].order_idx
    stepActions[index].order_idx = stepActions[swapIndex].order_idx
    stepActions[swapIndex].order_idx = temp

    try {
      await fetch(`${API_BASE_URL}/tables/engine_actions/update/${stepActions[index].id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ order_idx: stepActions[index].order_idx })
      })
      await fetch(`${API_BASE_URL}/tables/engine_actions/update/${stepActions[swapIndex].id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ order_idx: stepActions[swapIndex].order_idx })
      })
      loadEngineActions()
    } catch (error) {
      console.error('Failed to reorder:', error)
    }
  }

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // 8px movement required to start drag
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  // Handle drag end for reordering actions
  const handleDragEnd = async (event, stepActions) => {
    const { active, over } = event

    if (!over || active.id === over.id) return

    const oldIndex = stepActions.findIndex(a => a.id === active.id)
    const newIndex = stepActions.findIndex(a => a.id === over.id)

    if (oldIndex === -1 || newIndex === -1) return

    // Don't allow dragging past "- no condition -" (else) items
    const draggedAction = stepActions[oldIndex]
    const targetAction = stepActions[newIndex]

    if (draggedAction.array === '- no condition -') return
    if (targetAction.array === '- no condition -' && newIndex < stepActions.length - 1) return

    // Get reorderable actions (exclude "- no condition -" at the end)
    const reorderableActions = stepActions.filter(a => a.action_type === 'regular' && a.array !== '- no condition -')
    const elseAction = stepActions.find(a => a.array === '- no condition -')

    const reorderOldIndex = reorderableActions.findIndex(a => a.id === active.id)
    const reorderNewIndex = reorderableActions.findIndex(a => a.id === over.id)

    if (reorderOldIndex === -1 || reorderNewIndex === -1) return

    // Reorder the array
    const reordered = arrayMove(reorderableActions, reorderOldIndex, reorderNewIndex)

    // Update order_idx for all affected actions
    try {
      const updates = reordered.map((action, idx) => ({
        id: action.id,
        order_idx: idx + 1
      }))

      // Update each action sequentially to avoid overwhelming the server
      for (const action of updates) {
        await fetch(`${API_BASE_URL}/tables/engine_actions/update/${action.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ order_idx: action.order_idx })
        })
      }

      loadEngineActions()
    } catch (error) {
      console.error('Failed to reorder actions:', error)
    }
  }

  const copyFromPreviousStep = async (step) => {
    if (step.value <= 1) return
    setSaving(true)
    try {
      const prevActions = engineActions.filter(a => a.step_number === step.value - 1)
      for (const action of prevActions) {
        const newAction = { ...action, step_number: step.value }
        delete newAction.id
        await fetch(`${API_BASE_URL}/tables/engine_actions/add`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(newAction)
        })
      }
      loadEngineActions()
    } catch (error) {
      console.error('Failed to copy:', error)
    } finally {
      setSaving(false)
    }
  }

  // Toggle Whisper transcription for all actions in a step (for HYBRID transcriber)
  const toggleWhisperForStep = async (step) => {
    if (step.actions.length === 0) return
    setSaving(true)
    try {
      const newValue = step.useWhisper ? 0 : 1 // Toggle the value
      // Update all actions in this step
      for (const action of step.actions) {
        await fetch(`${API_BASE_URL}/tables/engine_actions/update/${action.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ use_whisper: newValue })
        })
      }
      loadEngineActions()
    } catch (error) {
      console.error('Failed to toggle Whisper for step:', error)
    } finally {
      setSaving(false)
    }
  }

  const getLabelDisplay = (tableName) => {
    const record = tables.find(t => t.table === tableName)
    return record?.label || tableName
  }

  // ========== SOUND FUNCTIONS ==========
  const openSoundModal = (sound) => {
    setModalType('sound')
    setModalMode('edit')
    setCurrentItem(sound)
    setFormData({
      name: sound?.name || '',
      selector: sound?.selector || '',
      duration_ms: sound?.duration_ms || 0,
      is_transfer_audio: sound?.is_transfer_audio || false
    })
    setHasChanged(false)
    setShowModal(true)
  }

  const saveSound = async () => {
    setSaving(true)
    try {
      await fetch(`${API_BASE_URL}/tables/sounds/update/${currentItem.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: currentItem.id,
          name: formData.name,
          selector: formData.selector,
          duration_ms: formData.duration_ms,
          is_transfer_audio: formData.is_transfer_audio
        })
      })
      loadSoundsRecords()
      loadSoundFiles()
      setShowModal(false)
    } catch (error) {
      console.error('Failed to save sound:', error)
    } finally {
      setSaving(false)
    }
  }

  const handleFileUpload = async (event) => {
    const files = event.target.files
    if (!files?.length) return

    setSaving(true)
    for (const file of files) {
      const formDataUpload = new FormData()
      formDataUpload.append('file', file)
      formDataUpload.append('avatar_id', id)

      try {
        await fetch(`${API_BASE_URL}/blob/uploadFile`, {
          method: 'POST',
          body: formDataUpload
        })
      } catch (error) {
        console.error('Upload failed:', error)
      }
    }
    setSaving(false)
    setTimeout(() => {
      loadSoundFiles()
      loadSoundsRecords()
    }, 1000)
  }

  const playAudio = (sound) => {
    if (playingSound === sound.id) {
      audioRef.current?.pause()
      setPlayingSound(null)
    } else {
      if (audioRef.current) {
        audioRef.current.src = BLOB_URL + sound.filename
        audioRef.current.play()
        setPlayingSound(sound.id)
      }
    }
  }

  const getFileData = async (filename) => {
    try {
      await fetch(`${API_BASE_URL}/blob/getSoundFileData`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ filename })
      })
      loadSoundsRecords()
      loadSoundFiles()
    } catch (error) {
      console.error('Failed to get file data:', error)
    }
  }

  // ========== TABS ==========
  const tabs = [
    { id: 'licenses', label: 'Licenses', icon: Key },
    { id: 'engine', label: 'Engine', icon: Settings }
    // Sounds tab hidden - functionality preserved but not exposed in UI
  ]

  if (loading) {
    return (
      <div className="loading-state">
        <div className="loading-spinner" />
        <p>Loading avatar...</p>
      </div>
    )
  }

  if (!avatar) {
    return (
      <div className="empty-state">
        <h3>Avatar not found</h3>
        <button className="btn btn-primary" onClick={() => navigate('/avatars')}>Back to Avatars</button>
      </div>
    )
  }

  // Merge sound files with sound records
  const mergedSounds = soundFiles.map(filename => {
    const record = soundsRecords.find(r => r.filename === filename)
    return record || { filename, name: filename.split('/').pop() }
  })

  return (
    <motion.div className="avatar-details" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
      {/* Hidden audio element */}
      <audio ref={audioRef} onEnded={() => setPlayingSound(null)} />

      {/* Breadcrumb */}
      <div className="breadcrumb">
        <Link to="/">Dashboard</Link>
        <ChevronRight size={16} />
        <Link to="/avatars">Avatars</Link>
        <ChevronRight size={16} />
        <span>{avatar?.name}</span>
      </div>

      {/* Header */}
      <div className="detail-header">
        <Link to="/avatars" className="back-link"><ArrowLeft size={20} /></Link>
        <div className="detail-avatar">{avatar?.name?.charAt(0).toUpperCase()}</div>
        <div className="detail-info">
          <h1 className="detail-title">{avatar?.name}</h1>
          <p style={{ color: 'var(--text-muted)', margin: 0 }}>{avatar?.company}, {avatar?.campaign}</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="tabs">
        {tabs.map(tab => (
          <motion.button
            key={tab.id}
            className={`tab ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
            whileHover={{ y: -2 }}
          >
            <tab.icon size={18} />
            {tab.label}
            {activeTab === tab.id && <motion.div className="tab-indicator" layoutId="avatar-tab" />}
          </motion.button>
        ))}
      </div>

      {/* Tab Content */}
      <AnimatePresence mode="wait">
        <motion.div key={activeTab} className="tab-content" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>

          {/* ========== LICENSES TAB ========== */}
          {activeTab === 'licenses' && (
            <div className="licenses-tab">
              <div className="tab-header">
                <h2>Licenses</h2>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button className="btn btn-primary btn-sm" onClick={() => openLicenseModal()}>
                    <Plus size={16} /> Add License
                  </button>
                </div>
              </div>

              {/* Linked Dialers Management */}
              <div style={{
                marginBottom: '16px',
                padding: '16px',
                background: 'var(--bg-card)',
                borderRadius: '8px',
                border: '1px solid var(--border-color)'
              }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: linkedDialers.length > 0 ? '12px' : '0'
                }}>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Linked Dialers
                  </div>
                  <button
                    className="btn btn-ghost btn-sm"
                    onClick={() => {
                      setModalType('link-dialer')
                      setFormData({})
                      setShowModal(true)
                    }}
                    style={{ fontSize: '0.75rem' }}
                  >
                    <Plus size={14} /> Link Dialer
                  </button>
                </div>

                {linkedDialers.length > 0 ? (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {linkedDialers.map(d => (
                      <span key={d.id} style={{
                        padding: '4px 10px',
                        background: 'var(--primary)',
                        color: 'white',
                        borderRadius: '999px',
                        fontSize: '0.75rem',
                        fontWeight: '500',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}>
                        {d.name}
                        <button
                          onClick={async () => {
                            if (confirm(`Unlink "${d.name}" from this avatar?`)) {
                              try {
                                await fetch(`${API_BASE_URL}/tables/dialers/update/${d.id}`, {
                                  method: 'PUT',
                                  headers: { 'Content-Type': 'application/json' },
                                  body: JSON.stringify({ avatar_id: null })
                                })
                                loadDialers()
                              } catch (err) {
                                alert('Failed to unlink dialer')
                              }
                            }
                          }}
                          style={{
                            background: 'transparent',
                            border: 'none',
                            color: 'white',
                            cursor: 'pointer',
                            padding: '0',
                            display: 'flex',
                            alignItems: 'center',
                            opacity: 0.7
                          }}
                          title="Unlink dialer"
                        >
                          <X size={12} />
                        </button>
                      </span>
                    ))}
                  </div>
                ) : (
                  <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', margin: '8px 0 0' }}>
                    No dialers linked. Link a dialer to enable Engine Actions setup.
                  </p>
                )}
              </div>

              {licenses.length === 0 ? (
                <div className="empty-state">
                  <Key size={48} />
                  <p>No licenses assigned to this avatar</p>
                  {linkedDialers.length > 0 && (
                    <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginTop: '8px' }}>
                      Click "Add License" to create licenses for the linked dialers above
                    </p>
                  )}
                </div>
              ) : (
                <div className="table-container">
                  {/* Bulk Actions Bar */}
                  {selectedLicenses.length > 0 && (
                    <div style={{
                      padding: '12px',
                      background: 'var(--bg-secondary)',
                      borderRadius: '8px 8px 0 0',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      borderBottom: '1px solid var(--border-color)'
                    }}>
                      <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                        {selectedLicenses.length} license{selectedLicenses.length > 1 ? 's' : ''} selected
                      </span>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button
                          className="btn btn-secondary btn-sm"
                          onClick={() => {
                            const keys = selectedLicenses.map(l => l.key_string).join('\n')
                            navigator.clipboard.writeText(keys)
                            alert(`${selectedLicenses.length} key(s) copied!`)
                          }}
                        >
                          <Copy size={14} /> Copy Keys
                        </button>
                        <button className="btn btn-danger btn-sm" onClick={deleteSelectedLicenses}>
                          <Trash2 size={14} /> Delete Selected
                        </button>
                      </div>
                    </div>
                  )}
                  <table className="table licenses-table">
                    <thead>
                      <tr>
                        <th style={{ width: '36px' }}>
                          <input
                            type="checkbox"
                            checked={selectedLicenses.length === licenses.length && licenses.length > 0}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedLicenses([...licenses])
                              } else {
                                setSelectedLicenses([])
                              }
                            }}
                            style={{ cursor: 'pointer' }}
                            title="Select all"
                          />
                        </th>
                        <th style={{ width: '50px' }}>Id</th>
                        <th style={{ width: '100px' }}>Dialer</th>
                        <th>Access Link</th>
                        <th style={{ width: '90px' }}>User</th>
                        <th style={{ width: '90px' }}>Password</th>
                        <th style={{ width: '80px' }}>Campaign</th>
                        <th style={{ width: '180px' }}>Key String</th>
                        <th style={{ width: '80px' }}>Last Used</th>
                        <th style={{ width: '50px' }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {licenses.map(lic => {
                        const lastUsed = getLicenseLastUsed(lic.id)
                        return (
                          <tr key={lic.id}>
                            <td>
                              <input
                                type="checkbox"
                                checked={selectedLicenses.some(l => l.id === lic.id)}
                                onChange={() => toggleLicenseSelection(lic)}
                                style={{ cursor: 'pointer' }}
                              />
                            </td>
                            <td>{lic.id}</td>
                            <td>{lic.dialer}</td>
                            <td style={{ maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis' }}>{lic.access_link}</td>
                            <td>{lic.user}</td>
                            <td>{lic.password}</td>
                            <td>{lic.campaign}</td>
                            <td>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <code style={{ fontSize: '0.65rem', maxWidth: '120px', overflow: 'hidden', textOverflow: 'ellipsis', display: 'block' }}>
                                  {lic.key_string}
                                </code>
                                <button
                                  className="btn btn-ghost btn-xs"
                                  onClick={() => {
                                    navigator.clipboard.writeText(lic.key_string)
                                    alert('Key copied!')
                                  }}
                                  title="Copy key"
                                >
                                  <Copy size={12} />
                                </button>
                              </div>
                            </td>
                            <td>
                              {lastUsed ? (
                                <span style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '6px',
                                  fontSize: '0.75rem',
                                  color: lastUsed.isActive ? '#22c55e' : 'var(--text-muted)',
                                  fontWeight: lastUsed.isActive ? 500 : 400
                                }}>
                                  {lastUsed.isActive && (
                                    <span style={{
                                      width: '6px',
                                      height: '6px',
                                      background: '#22c55e',
                                      borderRadius: '50%',
                                      animation: 'pulse 2s ease-in-out infinite'
                                    }} />
                                  )}
                                  {lastUsed.text}
                                </span>
                              ) : (
                                <span style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>—</span>
                              )}
                            </td>
                            <td>
                              <button className="btn btn-ghost btn-sm" onClick={() => openLicenseModal(lic)}>
                                <Edit size={14} />
                              </button>
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* ========== ENGINE TAB ========== */}
          {activeTab === 'engine' && (
            <div className="engine-tab">
              <div className="tab-header">
                <h2>Engine Manager</h2>
              </div>

              {/* Stats Dashboard */}
              <div className="stats-dashboard">
                <div className="stat-card">
                  <div className="stat-card-icon"><Zap size={18} /></div>
                  <div className="stat-card-content">
                    <div className="stat-card-value">{engineStats.regularCount}</div>
                    <div className="stat-card-label">Regular Actions</div>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-card-icon"><Clock size={18} /></div>
                  <div className="stat-card-content">
                    <div className="stat-card-value">{engineStats.silenceCount}</div>
                    <div className="stat-card-label">Silence Handlers</div>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-card-icon"><X size={18} /></div>
                  <div className="stat-card-content">
                    <div className="stat-card-value">{engineStats.notInterestedCount}</div>
                    <div className="stat-card-label">Not Interested</div>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-card-icon"><Target size={18} /></div>
                  <div className="stat-card-content">
                    <div className="stat-card-value">{engineStats.uniqueConditions}</div>
                    <div className="stat-card-label">Unique Conditions</div>
                  </div>
                </div>
              </div>

              {/* Voice, Hello & Step Limits - Compact Merged */}
              <div className="config-card">
                <div className="config-card-header">
                  <div className="config-card-title">
                    <div className="config-card-icon"><Volume2 size={14} /></div>
                    <span className="config-card-label">Engine Configuration</span>
                  </div>
                </div>
                <div className="config-card-body">
                  {/* Voice Set Selection */}
                  <div className="config-row-inline">
                    <div className="config-inline-item">
                      <span className="config-label-sm">Voice Set</span>
                      <div className="config-value-inline">
                        <select
                          className="input config-select-sm"
                          value={selectedVoiceSetId || ''}
                          onChange={(e) => {
                            const val = e.target.value ? parseInt(e.target.value) : null
                            setSelectedVoiceSetId(val)
                            updateAvatar({ voice_set_id: val, voice_mode: 'modern' })
                          }}
                        >
                          <option value="">-- Select Voice Set --</option>
                          {voiceSets.map(vs => (
                            <option key={vs.id} value={vs.id}>{vs.name}</option>
                          ))}
                        </select>
                        <Link to="/voice-sets" style={{ fontSize: '0.7rem', color: 'var(--primary)', whiteSpace: 'nowrap' }}>
                          Manage →
                        </Link>
                      </div>
                    </div>

                    {/* Say Hello Toggle */}
                    <div className="config-inline-item">
                      <span className="config-label-sm">Say Hello on Pickup</span>
                      <div className="config-value-inline">
                        <div
                          className={`toggle-switch-sm ${sayHelloOnPickup ? 'active' : ''}`}
                          onClick={() => { setSayHelloOnPickup(!sayHelloOnPickup); updateAvatar({ say_hello_on_pickup: !sayHelloOnPickup }) }}
                        />
                        <span className="toggle-label-sm">{sayHelloOnPickup ? 'On' : 'Off'}</span>
                      </div>
                    </div>
                  </div>

                  {/* Hello Categories (only shown when Say Hello is on and Voice Set selected) */}
                  {sayHelloOnPickup && selectedVoiceSetId && (
                    <div className="hello-config-compact">
                      <div className="hello-row">
                        <div className="hello-item">
                          <label className="hello-label">Hello 1</label>
                          <select className="input hello-select" value={helloCategoryOne} onChange={(e) => { setHelloCategoryOne(e.target.value); updateAvatar({ hello_audio_category_one: e.target.value }) }}>
                            {voiceSetCategories.map(c => <option key={c} value={c}>{c}</option>)}
                          </select>
                        </div>
                        <div className="hello-item hello-delay">
                          <label className="hello-label">Delay</label>
                          <input type="number" className="input hello-input" value={helloTimingOne} onChange={(e) => { setHelloTimingOne(parseInt(e.target.value) || 0); updateAvatar({ say_hello_timing_one: parseInt(e.target.value) || 0 }) }} />
                        </div>
                        <div className="hello-item">
                          <label className="hello-label">Hello 2</label>
                          <select className="input hello-select" value={helloCategoryTwo} onChange={(e) => { setHelloCategoryTwo(e.target.value); updateAvatar({ hello_audio_category_two: e.target.value }) }}>
                            {voiceSetCategories.map(c => <option key={c} value={c}>{c}</option>)}
                          </select>
                        </div>
                        <div className="hello-item hello-delay">
                          <label className="hello-label">Delay</label>
                          <input type="number" className="input hello-input" value={helloTimingTwo} onChange={(e) => { setHelloTimingTwo(parseInt(e.target.value) || 0); updateAvatar({ say_hello_timing_two: parseInt(e.target.value) || 0 }) }} />
                        </div>
                      </div>
                    </div>
                  )}

                  {sayHelloOnPickup && !selectedVoiceSetId && (
                    <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', margin: '8px 0 0 0' }}>
                      Select a Voice Set to configure hello categories.
                    </p>
                  )}

                  {/* Step Iteration Limits - Inline */}
                  <div className="step-limits-section">
                    <div className="step-limits-header">
                      <span className="config-label-sm">Step Iteration Limits</span>
                      <span style={{ fontSize: '0.65rem', color: 'var(--text-muted)' }}>Max repeats per step</span>
                    </div>
                    <div className="step-limits-inline">
                      {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(stepNum => (
                        <div key={stepNum} className="step-limit-mini">
                          <span className="step-num">{stepNum}</span>
                          <input
                            type="number"
                            className="step-input-mini"
                            min="0"
                            max="99"
                            value={stepIterationLimits[stepNum] || 0}
                            onChange={(e) => {
                              const newLimits = [...stepIterationLimits]
                              newLimits[stepNum] = parseInt(e.target.value) || 0
                              setStepIterationLimits(newLimits)
                              updateAvatar({ script_step_iterations_limit: JSON.stringify(newLimits) })
                            }}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Script Steps Section */}
              <div className="script-steps-section">
                <div className="script-steps-header">
                  <div className="script-steps-title">
                    <Layers size={16} />
                    Script Steps
                    <span className="script-steps-count">{engineStats.stepsWithActions} active</span>
                  </div>
                </div>

                {scriptSteps.map((step, stepIdx) => (
                  <div
                    key={step.value}
                    className={`script-step ${step.actions.length > 0 ? 'has-actions' : ''}`}
                  >
                    <div className="script-step-header">
                      <div className="script-step-title">
                        <div className="step-number">{step.value}</div>
                        <span className="step-name">{step.text}</span>
                        {step.previousSound && step.actions.length > 0 && (
                          <span className="step-previous-audio" title={step.previousSound}>"{step.previousSound}"</span>
                        )}
                      </div>

                      {step.actions.length > 0 && (
                        <div className="step-actions-badge">
                          {step.actions.length} action{step.actions.length !== 1 ? 's' : ''}
                        </div>
                      )}

                      {/* Whisper Toggle - for HYBRID transcriber mode */}
                      {step.actions.length > 0 && (
                        <div
                          className="step-whisper-toggle"
                          onClick={() => toggleWhisperForStep(step)}
                          title={step.useWhisper ? 'Whisper enabled for this step (click to disable)' : 'Enable Whisper for this step'}
                        >
                          <Zap size={12} className={step.useWhisper ? 'whisper-active' : ''} />
                          <span className="toggle-label-sm">Whisper</span>
                          <div className={`toggle-switch-sm ${step.useWhisper ? 'active' : ''}`} />
                        </div>
                      )}

                      <div className="step-header-buttons">
                        {stepIdx > 0 && step.actions.length === 0 && scriptSteps[stepIdx - 1].actions.length > 0 && (
                          <button className="btn btn-secondary btn-sm" onClick={() => copyFromPreviousStep(step)} disabled={saving}>
                            <Copy size={12} /> Copy
                          </button>
                        )}
                        {dataReady && (
                          <button className="btn btn-primary btn-sm" onClick={() => openActionModal(null, step.value)}>
                            <Plus size={12} />
                          </button>
                        )}
                      </div>
                    </div>

                    {step.actions.length === 0 ? (
                      <p className="step-empty">No actions configured for this step</p>
                    ) : (
                      <div className="script-step-body">
                        <DndContext
                          sensors={sensors}
                          collisionDetection={closestCenter}
                          onDragEnd={(event) => handleDragEnd(event, step.actions)}
                        >
                          <SortableContext
                            items={step.actions.map(a => a.id)}
                            strategy={verticalListSortingStrategy}
                          >
                            <div className="actions-list">
                              {step.actions.map((action, idx) => (
                                <SortableActionItem
                                  key={action.id}
                                  action={action}
                                  idx={idx}
                                  step={step}
                                  voiceMode={voiceMode}
                                  openActionModal={openActionModal}
                                  getLabelDisplay={getLabelDisplay}
                                  isDraggable={action.action_type === 'regular' && action.array !== '- no condition -'}
                                  voiceSetFilesByCategory={voiceSetFilesByCategory}
                                />
                              ))}
                            </div>
                          </SortableContext>
                        </DndContext>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ========== SOUNDS TAB ========== */}
          {activeTab === 'sounds' && (
            <div className="sounds-tab">
              <div className="tab-header">
                <h2>Sound Manager</h2>
                <div>
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileUpload}
                    accept="audio/wav, audio/wave"
                    multiple
                    style={{ display: 'none' }}
                  />
                  <button className="btn btn-primary btn-sm" onClick={() => fileInputRef.current?.click()} disabled={saving}>
                    {saving ? <><Loader2 size={16} className="spin" /> Uploading...</> : <><Upload size={16} /> Upload Files</>}
                  </button>
                </div>
              </div>

              {mergedSounds.length === 0 ? (
                <div className="empty-state">
                  <Volume2 size={48} />
                  <p>No sounds uploaded yet</p>
                </div>
              ) : (
                <div className="table-container" style={{ overflowX: 'auto' }}>
                  <table className="table" style={{ minWidth: '1200px' }}>
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Duration (ms)</th>
                        <th>Selector</th>
                        <th>Filename</th>
                        <th>Sample Rate</th>
                        <th>Num. Channels</th>
                        <th>Byte Rate</th>
                        <th>Bits/Sample</th>
                        <th>Xfer</th>
                        <th>Transcription</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {mergedSounds.map((sound, idx) => (
                        <tr key={sound.id || idx}>
                          <td>{sound.name || '-'}</td>
                          <td>{sound.duration_ms || '-'}</td>
                          <td style={{ maxWidth: '100px', overflow: 'hidden', textOverflow: 'ellipsis' }}>{sound.selector || '-'}</td>
                          <td><code style={{ fontSize: '0.65rem' }}>{sound.filename}</code></td>
                          <td>{sound.sample_rate || '-'}</td>
                          <td>{sound.number_of_channels || '-'}</td>
                          <td>{sound.byte_rate || '-'}</td>
                          <td>{sound.bits_per_sample || '-'}</td>
                          <td>{sound.is_transfer_audio ? <Check size={14} color="var(--success)" /> : ''}</td>
                          <td style={{ maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis' }}>{sound.transcription || '-'}</td>
                          <td>
                            <div style={{ display: 'flex', gap: '4px' }}>
                              <button className="btn btn-ghost btn-sm" onClick={() => playAudio(sound)}>
                                {playingSound === sound.id ? <Pause size={14} /> : <Play size={14} />}
                              </button>
                              <button className="btn btn-ghost btn-sm" onClick={() => getFileData(sound.filename)}>Get data</button>
                              {sound.id && <button className="btn btn-ghost btn-sm" onClick={() => openSoundModal(sound)}>Edit</button>}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </motion.div>
      </AnimatePresence>

      {/* ========== MODALS ========== */}
      <AnimatePresence>
        {showModal && (
          <motion.div className="modal-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setShowModal(false)}>
            <motion.div
              className="modal"
              style={{ maxWidth: modalType === 'action' ? '600px' : '500px' }}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              onClick={(e) => { e.stopPropagation(); setOpenDropdown(null) }}
            >
              <div className="modal-header">
                <h2 className="modal-title">
                  {modalType === 'link-dialer' ? 'Link Dialer to Avatar' : (
                    <>{modalMode === 'add' ? 'Add' : 'Edit'} {modalType === 'license' ? 'License' : modalType === 'action' ? `Action - Step ${formData.step_number}` : 'Record'}</>
                  )}
                </h2>
                <button className="modal-close" onClick={() => setShowModal(false)}><X size={20} /></button>
              </div>

              {/* LINK DIALER FORM */}
              {modalType === 'link-dialer' ? (
                <div className="modal-body">
                  <p style={{ marginBottom: '16px', color: 'var(--text-muted)', fontSize: '0.9rem' }}>
                    Select a dialer to link to this avatar. This allows you to set up Engine Actions and create licenses.
                  </p>
                  <div className="input-group">
                    <label className="input-label">Available Dialers</label>
                    <select
                      className="input"
                      value={formData.dialer_id || ''}
                      onChange={(e) => setFormData({ ...formData, dialer_id: e.target.value })}
                    >
                      <option value="">Select a dialer...</option>
                      {dialers
                        .filter(d => !linkedDialers.find(ld => ld.id === d.id))
                        .map(d => (
                          <option key={d.id} value={d.id}>{d.name}</option>
                        ))
                      }
                    </select>
                  </div>
                  <div className="modal-footer" style={{ marginTop: '24px' }}>
                    <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                    <button
                      type="button"
                      className="btn btn-primary"
                      disabled={!formData.dialer_id || saving}
                      onClick={async () => {
                        if (!formData.dialer_id) return
                        setSaving(true)
                        try {
                          await fetch(`${API_BASE_URL}/tables/dialers/update/${formData.dialer_id}`, {
                            method: 'PUT',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ avatar_id: parseInt(id) })
                          })
                          loadDialers()
                          setShowModal(false)
                        } catch (err) {
                          alert('Failed to link dialer: ' + err.message)
                        } finally {
                          setSaving(false)
                        }
                      }}
                    >
                      {saving ? <><Loader2 size={16} className="spin" /> Linking...</> : 'Link Dialer'}
                    </button>
                  </div>
                </div>
              ) : (

                <form onSubmit={(e) => { e.preventDefault(); modalType === 'license' ? saveLicense() : modalType === 'action' ? saveAction() : saveSound() }}>
                  <div className="modal-body">
                    {/* LICENSE FORM */}
                    {modalType === 'license' && (
                      <>
                        <div className="input-group">
                          <label className="input-label">Dialer</label>
                          {linkedDialers.length > 0 && (
                            <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '6px' }}>
                              💡 Dialers linked to this avatar are shown first
                            </div>
                          )}
                          <SearchableSelect
                            options={[
                              // Linked dialers first
                              ...linkedDialers.map(d => d.name),
                              // Then other dialers
                              ...dialers.filter(d => !linkedDialers.find(ld => ld.id === d.id)).map(d => d.name)
                            ]}
                            value={dialers.find(d => d.id == formData.dialer_id)?.name || ''}
                            onChange={(val) => {
                              const dialer = dialers.find(d => d.name === val)
                              setFormData({ ...formData, dialer_id: dialer?.id || '' })
                              setHasChanged(true)
                            }}
                            placeholder="Select dialer..."
                            field="dialer_select"
                          />
                        </div>
                        <div className="input-group">
                          <label className="input-label">Access Link</label>
                          <SearchableSelect
                            options={accessLinks}
                            value={formData.access_link || ''}
                            onChange={(val) => { setFormData({ ...formData, access_link: val }); setHasChanged(true) }}
                            placeholder="Select access link..."
                            field="access_link_select"
                          />
                        </div>
                        <div className="input-group">
                          <label className="input-label">User</label>
                          <input type="text" className="input" value={formData.user || ''} onChange={e => { setFormData({ ...formData, user: e.target.value }); setHasChanged(true) }} required />
                        </div>
                        <div className="input-group">
                          <label className="input-label">Password</label>
                          <input type="text" className="input" value={formData.password || ''} onChange={e => { setFormData({ ...formData, password: e.target.value }); setHasChanged(true) }} required />
                        </div>
                        <div className="input-group">
                          <label className="input-label">Campaign</label>
                          <input type="text" className="input" value={formData.campaign || ''} onChange={e => { setFormData({ ...formData, campaign: e.target.value }); setHasChanged(true) }} />
                        </div>

                        {/* Voice Set Pool - Per-call voice rotation */}
                        {voiceMode === 'modern' && voiceSets.length > 0 && (
                          <div className="input-group" style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid var(--border-color)' }}>
                            <label className="input-label" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <Shuffle size={14} style={{ color: 'var(--primary)' }} />
                              Voice Set Pool
                            </label>
                            <p style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '8px', marginTop: 0 }}>
                              Select multiple voice sets for per-call rotation. Bot randomly picks a voice for each call.
                            </p>

                            {/* Selected Voice Sets Tags */}
                            <div style={{
                              display: 'flex',
                              flexWrap: 'wrap',
                              gap: '6px',
                              marginBottom: '8px',
                              minHeight: '36px',
                              padding: '8px',
                              background: 'var(--bg-secondary)',
                              borderRadius: '6px',
                              border: '1px solid var(--border-color)'
                            }}>
                              {formData.voice_set_pool && formData.voice_set_pool.length > 0 ? (
                                formData.voice_set_pool.map(vsId => {
                                  const vs = voiceSets.find(v => v.id === vsId);
                                  return (
                                    <span
                                      key={vsId}
                                      style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '4px',
                                        padding: '4px 10px',
                                        background: 'var(--primary)',
                                        color: 'white',
                                        borderRadius: '4px',
                                        fontSize: '0.75rem',
                                        fontWeight: 500
                                      }}
                                    >
                                      {vs?.name || `ID: ${vsId}`}
                                      <X
                                        size={14}
                                        style={{ cursor: 'pointer', opacity: 0.8, marginLeft: '2px' }}
                                        onClick={() => {
                                          setFormData({
                                            ...formData,
                                            voice_set_pool: formData.voice_set_pool.filter(id => id !== vsId)
                                          });
                                          setHasChanged(true);
                                        }}
                                      />
                                    </span>
                                  );
                                })
                              ) : (
                                <span style={{ color: 'var(--text-muted)', fontSize: '0.75rem', padding: '4px 0' }}>
                                  No voice sets selected — will use avatar's default voice set
                                </span>
                              )}
                            </div>

                            {/* Add Voice Set Dropdown */}
                            <SearchableSelect
                              options={voiceSets
                                .filter(vs => !(formData.voice_set_pool || []).includes(vs.id))
                                .map(vs => vs.name)}
                              value=""
                              onChange={(val) => {
                                const vs = voiceSets.find(v => v.name === val);
                                if (vs && !(formData.voice_set_pool || []).includes(vs.id)) {
                                  setFormData({
                                    ...formData,
                                    voice_set_pool: [...(formData.voice_set_pool || []), vs.id]
                                  });
                                  setHasChanged(true);
                                }
                              }}
                              placeholder="+ Add voice set to pool..."
                              field="voice_set_pool_select"
                            />

                            {formData.voice_set_pool && formData.voice_set_pool.length > 0 && (
                              <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                marginTop: '8px',
                                padding: '8px 12px',
                                background: 'rgba(139, 92, 246, 0.1)',
                                borderRadius: '6px',
                                fontSize: '0.7rem',
                                color: 'var(--primary)'
                              }}>
                                <Shuffle size={12} />
                                <span>
                                  <strong>{formData.voice_set_pool.length}</strong> voice set{formData.voice_set_pool.length > 1 ? 's' : ''} in rotation —
                                  bot will randomly select one per call
                                </span>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Bot Configuration Section */}
                        <div style={{ borderTop: '1px solid var(--border-color)', marginTop: '16px', paddingTop: '16px' }}>
                          <button
                            type="button"
                            onClick={() => setFormData({ ...formData, _showBotConfig: !formData._showBotConfig })}
                            style={{
                              background: 'none',
                              border: 'none',
                              color: 'var(--text-secondary)',
                              cursor: 'pointer',
                              fontSize: '0.85rem',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px',
                              padding: 0
                            }}
                          >
                            {formData._showBotConfig ? '▼' : '▶'} Bot Configuration
                          </button>

                          {formData._showBotConfig && (
                            <div style={{ marginTop: '12px', background: 'var(--bg-secondary)', padding: '12px', borderRadius: '8px' }}>
                              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                                <div className="input-group" style={{ marginBottom: '8px' }}>
                                  <label className="input-label" style={{ fontSize: '0.75rem' }}>Login Method</label>
                                  <SearchableSelect
                                    options={['single-v1', 'single-v2', 'repeating', 'double', 'double-merged', 'second-try', 'at-law', 'double-ziscom']}
                                    value={formData.login_method || 'double-merged'}
                                    onChange={(val) => { setFormData({ ...formData, login_method: val }); setHasChanged(true) }}
                                    placeholder="Select login method..."
                                    field="login_method_select"
                                  />
                                </div>
                                <div className="input-group" style={{ marginBottom: '8px' }}>
                                  <label className="input-label" style={{ fontSize: '0.75rem' }}>Transfer Method</label>
                                  <SearchableSelect
                                    options={['dynamic', 'inboundlocal', 'd1', 'd2', 'd3', 'd4', 'd5', 'd2-preset', 'Quick', 'local', 'local-group', 'preset-dial-with-customer', 'dial-with-customer', 'd1-preset', 'd4-preset', 'preset-voice', 'd1-cons-dwc', 'd1-dial-with-customer', 'localcloserconsultative']}
                                    value={formData.transfer_method || 'd1'}
                                    onChange={(val) => { setFormData({ ...formData, transfer_method: val }); setHasChanged(true) }}
                                    placeholder="Select transfer method..."
                                    field="transfer_method_select"
                                  />
                                </div>
                                <div className="input-group" style={{ marginBottom: '8px' }}>
                                  <label className="input-label" style={{ fontSize: '0.75rem' }}>Transfer Disposition</label>
                                  <SearchableSelect
                                    options={['call-transfered', 'sale-made']}
                                    value={formData.xfer_disposition_selector || 'call-transfered'}
                                    onChange={(val) => { setFormData({ ...formData, xfer_disposition_selector: val }); setHasChanged(true) }}
                                    placeholder="Select disposition..."
                                    field="xfer_disposition_select"
                                  />
                                </div>
                                <div className="input-group" style={{ marginBottom: '8px' }}>
                                  <label className="input-label" style={{ fontSize: '0.75rem' }}>Transcriber</label>
                                  <SearchableSelect
                                    options={['VOSK', 'VOSK_V15L', 'WHISPER_CPU']}
                                    value={formData.transcriber_type || 'VOSK'}
                                    onChange={(val) => { setFormData({ ...formData, transcriber_type: val }); setHasChanged(true) }}
                                    placeholder="Select transcriber..."
                                    field="transcriber_select"
                                  />
                                </div>
                              </div>

                              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px', marginTop: '12px' }}>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.75rem', cursor: 'pointer' }}>
                                  <input type="checkbox" checked={formData.dont_wait !== false && formData.dont_wait !== 0} onChange={e => { setFormData({ ...formData, dont_wait: e.target.checked }); setHasChanged(true) }} />
                                  Don't Wait
                                </label>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.75rem', cursor: 'pointer' }}>
                                  <input type="checkbox" checked={formData.comment_login_submit_btn !== false && formData.comment_login_submit_btn !== 0} onChange={e => { setFormData({ ...formData, comment_login_submit_btn: e.target.checked }); setHasChanged(true) }} />
                                  Skip Login Btn
                                </label>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.75rem', cursor: 'pointer' }}>
                                  <input type="checkbox" checked={formData.comment_2nd_hello_logic !== false && formData.comment_2nd_hello_logic !== 0} onChange={e => { setFormData({ ...formData, comment_2nd_hello_logic: e.target.checked }); setHasChanged(true) }} />
                                  Skip 2nd Hello
                                </label>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.75rem', cursor: 'pointer' }}>
                                  <input type="checkbox" checked={formData.cmnt_40sec_call_limit_check === true || formData.cmnt_40sec_call_limit_check === 1} onChange={e => { setFormData({ ...formData, cmnt_40sec_call_limit_check: e.target.checked }); setHasChanged(true) }} />
                                  Skip 40s Limit
                                </label>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.75rem', cursor: 'pointer' }}>
                                  <input type="checkbox" checked={formData.dispo_in_cb_after_40sec === true || formData.dispo_in_cb_after_40sec === 1} onChange={e => { setFormData({ ...formData, dispo_in_cb_after_40sec: e.target.checked }); setHasChanged(true) }} />
                                  Dispo CB 40s
                                </label>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.75rem', cursor: 'pointer' }}>
                                  <input type="checkbox" checked={formData.debug_mode === true || formData.debug_mode === 1} onChange={e => { setFormData({ ...formData, debug_mode: e.target.checked }); setHasChanged(true) }} />
                                  Debug Mode
                                </label>
                              </div>
                            </div>
                          )}
                        </div>

                        {currentItem && (
                          <div style={{ marginTop: '16px', textAlign: 'right' }}>
                            <button type="button" className="btn btn-danger btn-sm" onClick={() => deleteLicense(currentItem)}>
                              <Trash2 size={16} /> Delete
                            </button>
                          </div>
                        )}
                      </>
                    )}

                    {/* ACTION FORM */}
                    {modalType === 'action' && (
                      <>
                        <div className="input-group">
                          <label className="input-label">Action Type</label>
                          <div style={{ display: 'flex', gap: '4px' }}>
                            {actionTypes.map(at => (
                              <button
                                key={at.value}
                                type="button"
                                className={`btn btn-sm ${formData.action_type === at.value ? 'btn-primary' : 'btn-secondary'}`}
                                onClick={() => { setFormData({ ...formData, action_type: at.value }); setHasChanged(true) }}
                              >
                                {at.label}
                              </button>
                            ))}
                          </div>
                        </div>

                        {formData.action_type === 'regular' && (
                          <div className="input-group">
                            <label className="input-label">Condition Array</label>
                            <SearchableSelect
                              options={tables.map(t => t.table)}
                              value={formData.array || '- no condition -'}
                              onChange={(val) => { setFormData({ ...formData, array: val }); setHasChanged(true) }}
                              placeholder="Select condition..."
                              field="array"
                            />
                          </div>
                        )}

                        {formData.action_type === 'on_silence' && (
                          <>
                            <div className="input-group">
                              <label className="input-label">Silence Counter</label>
                              <input type="number" className="input" value={formData.silence_counter || 0} onChange={e => { setFormData({ ...formData, silence_counter: parseInt(e.target.value) || 0 }); setHasChanged(true) }} />
                            </div>
                            <div className="input-group">
                              <label className="input-label">Increase Step</label>
                              <div style={{ display: 'flex', gap: '4px' }}>
                                <button type="button" className={`btn btn-sm ${formData.increase_step ? 'btn-primary' : 'btn-secondary'}`} onClick={() => { setFormData({ ...formData, increase_step: true }); setHasChanged(true) }}>true</button>
                                <button type="button" className={`btn btn-sm ${!formData.increase_step ? 'btn-primary' : 'btn-secondary'}`} onClick={() => { setFormData({ ...formData, increase_step: false }); setHasChanged(true) }}>false</button>
                              </div>
                            </div>
                          </>
                        )}

                        <div className="input-group">
                          <label className="input-label">{voiceMode === 'modern' ? 'Audio Categories' : 'Audio Files'}</label>

                          {voiceMode === 'classic' ? (
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px' }}>
                              <div>
                                <label style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '4px', display: 'block' }}>Audio 1</label>
                                <SearchableSelect
                                  options={sounds}
                                  value={formData.audio_1 || '- none -'}
                                  onChange={(val) => { setFormData({ ...formData, audio_1: val }); setHasChanged(true) }}
                                  placeholder="Select audio..."
                                  field="audio_1"
                                />
                              </div>
                              <div>
                                <label style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '4px', display: 'block' }}>Audio 2</label>
                                <SearchableSelect
                                  options={sounds}
                                  value={formData.audio_2 || '- none -'}
                                  onChange={(val) => { setFormData({ ...formData, audio_2: val }); setHasChanged(true) }}
                                  placeholder="Select audio..."
                                  field="audio_2"
                                />
                              </div>
                              <div>
                                <label style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '4px', display: 'block' }}>Audio 3</label>
                                <SearchableSelect
                                  options={sounds}
                                  value={formData.audio_3 || '- none -'}
                                  onChange={(val) => { setFormData({ ...formData, audio_3: val }); setHasChanged(true) }}
                                  placeholder="Select audio..."
                                  field="audio_3"
                                />
                              </div>
                            </div>
                          ) : (
                            <>
                              {!selectedVoiceSetId ? (
                                <div style={{ padding: '12px', background: 'rgba(245, 158, 11, 0.1)', borderRadius: '8px', fontSize: '0.85rem', color: 'var(--warning)' }}>
                                  ⚠️ Select a Voice Set first to choose categories.
                                </div>
                              ) : (
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px' }}>
                                  <div>
                                    <label style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '4px', display: 'block' }}>Category 1</label>
                                    <SearchableSelect
                                      options={voiceSetCategories}
                                      value={formData.audio_category_1 || '- none -'}
                                      onChange={(val) => { setFormData({ ...formData, audio_category_1: val }); setHasChanged(true) }}
                                      placeholder="Select category..."
                                      field="audio_category_1"
                                    />
                                  </div>
                                  <div>
                                    <label style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '4px', display: 'block' }}>Category 2</label>
                                    <SearchableSelect
                                      options={voiceSetCategories}
                                      value={formData.audio_category_2 || '- none -'}
                                      onChange={(val) => { setFormData({ ...formData, audio_category_2: val }); setHasChanged(true) }}
                                      placeholder="Select category..."
                                      field="audio_category_2"
                                    />
                                  </div>
                                  <div>
                                    <label style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '4px', display: 'block' }}>Category 3</label>
                                    <SearchableSelect
                                      options={voiceSetCategories}
                                      value={formData.audio_category_3 || '- none -'}
                                      onChange={(val) => { setFormData({ ...formData, audio_category_3: val }); setHasChanged(true) }}
                                      placeholder="Select category..."
                                      field="audio_category_3"
                                    />
                                  </div>
                                </div>
                              )}
                              {selectedVoiceSetId && (
                                <p style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: '8px' }}>
                                  <Shuffle size={10} /> Bot will randomly select one variation from each category.
                                </p>
                              )}
                            </>
                          )}
                        </div>

                        {/* Interrupt/Continue Audio Categories - Modern mode only */}
                        {voiceMode === 'modern' && selectedVoiceSetId && (
                          <div className="input-group">
                            <label className="input-label" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <Zap size={14} style={{ color: 'var(--warning)' }} />
                              Interruption Handling
                            </label>
                            <p style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '8px', marginTop: 0 }}>
                              When user interrupts bot mid-speech, these audios control the conversation flow.
                            </p>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                              <div>
                                <label style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '4px', display: 'block' }}>
                                  🛑 Interrupt Audio
                                </label>
                                <SearchableSelect
                                  options={voiceSetCategories}
                                  value={formData.interrupt_audio_category || '- none -'}
                                  onChange={(val) => { setFormData({ ...formData, interrupt_audio_category: val }); setHasChanged(true) }}
                                  placeholder="e.g., Sorry Go Ahead"
                                  field="interrupt_audio_category"
                                />
                                <span style={{ fontSize: '0.65rem', color: 'var(--text-muted)' }}>Played when user interrupts</span>
                              </div>
                              <div>
                                <label style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '4px', display: 'block' }}>
                                  ▶️ Continue Audio
                                </label>
                                <SearchableSelect
                                  options={voiceSetCategories}
                                  value={formData.continue_audio_category || '- none -'}
                                  onChange={(val) => { setFormData({ ...formData, continue_audio_category: val }); setHasChanged(true) }}
                                  placeholder="e.g., As I Was Saying"
                                  field="continue_audio_category"
                                />
                                <span style={{ fontSize: '0.65rem', color: 'var(--text-muted)' }}>Played before resuming (if no keyword match)</span>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Can Interrupt Checkbox - Available in both modes */}
                        <div className="input-group">
                          <label
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              cursor: 'pointer',
                              padding: '12px',
                              background: formData.can_interrupt === false || formData.can_interrupt === 0
                                ? 'rgba(239, 68, 68, 0.1)'
                                : 'rgba(34, 197, 94, 0.1)',
                              borderRadius: '8px',
                              border: `1px solid ${formData.can_interrupt === false || formData.can_interrupt === 0
                                ? 'rgba(239, 68, 68, 0.3)'
                                : 'rgba(34, 197, 94, 0.3)'}`
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={formData.can_interrupt !== false && formData.can_interrupt !== 0}
                              onChange={e => {
                                setFormData({ ...formData, can_interrupt: e.target.checked ? 1 : 0 });
                                setHasChanged(true)
                              }}
                              style={{ width: '18px', height: '18px' }}
                            />
                            <div>
                              <span style={{ fontWeight: 500 }}>
                                {formData.can_interrupt === false || formData.can_interrupt === 0
                                  ? '🔒 Non-Interruptible'
                                  : '🔓 Interruptible'}
                              </span>
                              <p style={{ fontSize: '0.7rem', color: 'var(--text-muted)', margin: '4px 0 0 0' }}>
                                {formData.can_interrupt === false || formData.can_interrupt === 0
                                  ? 'Customer speech will NOT interrupt this audio (e.g., hello, transfer)'
                                  : 'Customer can interrupt this audio by speaking'}
                              </p>
                            </div>
                          </label>
                        </div>

                        <div className="input-group">
                          <label className="input-label">Emit Event</label>
                          {events.length === 0 ? (
                            <div className="events-warning">
                              <AlertCircle size={14} />
                              No events available. Assign a license with a dialer that has dispositions.
                            </div>
                          ) : (
                            <SearchableSelect
                              options={['', ...events]}
                              value={formData.emit || ''}
                              onChange={(val) => { setFormData({ ...formData, emit: val }); setHasChanged(true) }}
                              placeholder="-- No event --"
                              field="emit"
                            />
                          )}
                        </div>

                        {currentItem && (
                          <div style={{ marginTop: '16px', textAlign: 'right' }}>
                            <button type="button" className="btn btn-danger btn-sm" onClick={() => deleteAction(currentItem)}>
                              <Trash2 size={16} /> Delete
                            </button>
                          </div>
                        )}
                      </>
                    )}

                    {/* SOUND FORM */}
                    {modalType === 'sound' && (
                      <>
                        <div className="input-group">
                          <label className="input-label">Sound Name</label>
                          <input type="text" className="input" value={formData.name || ''} onChange={e => { setFormData({ ...formData, name: e.target.value }); setHasChanged(true) }} required />
                        </div>
                        <div className="input-group">
                          <label className="input-label">Selector</label>
                          <input type="text" className="input" value={formData.selector || ''} onChange={e => { setFormData({ ...formData, selector: e.target.value }); setHasChanged(true) }} />
                        </div>
                        <div className="input-group">
                          <label className="input-label">Duration (ms)</label>
                          <input type="number" className="input" value={formData.duration_ms || 0} onChange={e => { setFormData({ ...formData, duration_ms: parseInt(e.target.value) || 0 }); setHasChanged(true) }} />
                        </div>
                        <div className="input-group">
                          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                            <input type="checkbox" checked={formData.is_transfer_audio || false} onChange={e => { setFormData({ ...formData, is_transfer_audio: e.target.checked }); setHasChanged(true) }} />
                            <span>Is Transfer Audio</span>
                          </label>
                        </div>
                      </>
                    )}
                  </div>

                  <div className="modal-footer">
                    <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                    <button type="submit" className="btn btn-primary" disabled={saving || !hasChanged} style={{ visibility: hasChanged ? 'visible' : 'hidden' }}>
                      {saving ? <><Loader2 size={18} className="spin" /> Saving...</> : <><Check size={18} /> Save</>}
                    </button>
                  </div>
                </form>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  )
}

export default AvatarDetails